import { Card, CardBody, Icon, SimpleGrid, Stack, Text, Box } from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import {
  Clock,
  Database,
  Flag,
  FileArchive,
  Languages,
  Sparkles,
  Gauge,
  Sprout,
} from 'lucide-react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useMemo } from 'react';
import { withPermission } from '../withPermission';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export const DevelopersPage = withPermission('canAccessDeveloperTools', () => {
  const developerPages = useMemo(
    () => [
      {
        title: t`AI Playground`,
        description: t`Test AI capabilities: text generation, image generation, speech, and transcription.`,
        path: '/developer-tools/ai-playground',
        icon: Sparkles,
      },
      {
        title: t`Feature Flags`,
        description: t`Manage and debug feature flags, refresh caches, and compare local vs remote evaluation.`,
        path: '/developer-tools/feature-flags',
        icon: Flag,
      },
      {
        title: t`Data Migrations`,
        description: t`Run and monitor data migration procedures.`,
        path: '/developer-tools/data-migrations',
        icon: Database,
      },
      {
        title: t`Seed Data`,
        description: t`Run seed strategies for local development and monitor seed run history.`,
        path: '/developer-tools/seeds',
        icon: Sprout,
      },
      {
        title: t`Translation Queue`,
        description: t`System-wide view of translation generation: queue depth, in-flight runs, failures, and stuck-job tools.`,
        path: '/developer-tools/translation-queue',
        icon: Languages,
      },
      {
        title: t`DB Stats`,
        description: t`Run read-only Postgres stat-view queries: sequential scans, cache residency, live activity, and more.`,
        path: '/developer-tools/db-stats',
        icon: Gauge,
      },
      {
        title: t`Scheduled Tasks`,
        description: t`View and monitor scheduled task executions.`,
        path: '/scheduled-tasks',
        icon: Clock,
      },
      {
        title: t`File Upload`,
        description: t`Upload and manage files.`,
        path: '/developer-tools/files',
        icon: FileArchive,
      },
    ],
    [],
  );

  return (
    <Stack gap="0" h="full" w="full" bg="bg.surface">
      <Box p="4">
        <PageHeading>
          <Trans>Developer Tools</Trans>
        </PageHeading>
      </Box>
      <Box px="4" pb="4">
        <Text color="fg.muted" fontSize="sm">
          <Trans>Administrative tools for developers.</Trans>
        </Text>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap="4" px="4" pb="4">
        {developerPages.map((page) => {
          const PageIcon = page.icon;
          return (
            <Card
              key={page.path}
              as={Link}
              to={page.path}
              _hover={{ shadow: 'sm' }}
              cursor="pointer"
              borderWidth="1px"
              borderColor="border.default"
            >
              <CardBody>
                <Stack gap="2">
                  <Icon as={PageIcon} boxSize={iconSize.xl} color="brand.500" />
                  <Text fontWeight="semibold">{page.title}</Text>
                  <Text fontSize="sm" color="fg.muted">
                    {page.description}
                  </Text>
                </Stack>
              </CardBody>
            </Card>
          );
        })}
      </SimpleGrid>
    </Stack>
  );
});
